import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useApi } from '@/providers/ApiContext';
import {
  createChartWaypoint,
  updateChartWaypoint,
} from '@/actions/chartWaypoint';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import type { ChartWaypoint, EntityId } from '@/types';

interface ChartWaypointFormProps {
  open: boolean;
  onClose: () => void;
  chartId: EntityId;
  waypoint?: ChartWaypoint | null;
  onWaypointCreated?: () => void;
}

function ChartWaypointForm({
  open,
  onClose,
  chartId,
  waypoint,
  onWaypointCreated,
}: ChartWaypointFormProps): React.JSX.Element {
  const api = useApi();
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const isEditMode = Boolean(waypoint);

  useEffect(() => {
    if (open) {
      if (waypoint) {
        setName(waypoint.name || '');
      } else {
        setName('');
      }
      setNameError(false);
      setApiError(null);
    }
  }, [open, waypoint]);

  const handleSubmit = async (): Promise<void> => {
    if (!name.trim()) {
      setNameError(true);
      return;
    }

    try {
      setLoading(true);
      setApiError(null);

      if (!api) return;
      if (isEditMode && waypoint) {
        await updateChartWaypoint(api, chartId, waypoint.entity_id, {
          name: name.trim(),
        });
      } else {
        await createChartWaypoint(api, chartId, {
          name: name.trim(),
          order: 0, // Will be set by the server
        });
      }

      if (onWaypointCreated) {
        onWaypointCreated();
      }
    } catch (error) {
      console.error('Error saving waypoint:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          `Unable to ${isEditMode ? 'update' : 'create'} the waypoint. This could be due to network issues, server problems, or invalid data.`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (): void => {
    if (!loading) {
      setName('');
      setNameError(false);
      setApiError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Waypoint' : 'Add Waypoint'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label='Waypoint Name'
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(false);
            }}
            error={nameError}
            helperText={nameError ? 'Waypoint name is required' : ''}
            margin='normal'
            disabled={loading}
            autoFocus
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant='contained'
          disabled={!name.trim()}
        >
          {isEditMode ? 'Update' : 'Create'} Waypoint
        </LoadingButton>
      </DialogActions>
      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Dialog>
  );
}

export default ChartWaypointForm;

